import {
  type ModalQueueGeneratedOpener,
  useModalQueue
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useMemo } from 'react';
import { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';

type ContextType = {
  isAnyModalOpen: boolean;
  isContactModalOpen: boolean;
  setContactModalOpen: ModalQueueGeneratedOpener;
};

const defaultContext: ContextType = {
  isAnyModalOpen: false,
  isContactModalOpen: false,
  setContactModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }: React.PropsWithChildren) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);

  const { setContactModalOpen } = useMemo(() => {
    return {
      setContactModalOpen: generateOpener(ModalId.Contact)
    };
  }, [generateOpener]);

  const value = useMemo(
    () => ({
      isAnyModalOpen: currentModalId > -1,
      isContactModalOpen: currentModalId === ModalId.Contact,
      setContactModalOpen
    }),
    [currentModalId, setContactModalOpen]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useModalContext() {
  return React.useContext(ModalContext);
}
